// The "Gå till" box as a 3D scene (owner 2026-11-02, mockup goto-3d-v1): the site's published
// layout flown through to the bin the operator is sent to, with what stands in the bins. Shown
// only when the site has a published layout that describes the code; otherwise the caller's
// fallback (today's box) is rendered unchanged. The three.js module is loaded on first use.
//
// Data and its age: the published layout is cached in localStorage and refreshed once per page
// load; the occupancy (container projection) is read once per page load and kept for the
// session, so the bins show the stock as of that moment, not live.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Api, type LayoutOut } from "../api/client.js";
import { t } from "../i18n.js";
import { loadCachedBundle } from "../receive/registry.js";
import type { BinStock, MountedScene, RackTypeLike, SceneOptions } from "./goto-scene.js";

const LAYOUT_KEY = "trail.layout.published";

let layoutPromise: Promise<LayoutOut | null> | null = null;
let layoutAt = 0;
/** A published layout is re-read after this long in a running app (the owner republished and saw the old racks, 2026-11-02). */
const LAYOUT_MAX_AGE = 2 * 60_000;
let stockPromise: Promise<Map<string, BinStock>> | null = null;

function cachedLayout(): LayoutOut | null {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    return raw ? (JSON.parse(raw) as LayoutOut) : null;
  } catch {
    return null;
  }
}

/** The published layout from the server, at most once per couple of minutes; the cached copy when offline. */
function loadLayout(): Promise<LayoutOut | null> {
  if (layoutPromise && Date.now() - layoutAt < LAYOUT_MAX_AGE) return layoutPromise;
  layoutAt = Date.now();
  layoutPromise = Api.layoutPublished()
    .then((r) => {
      const l = r.published;
      try {
        if (l) localStorage.setItem(LAYOUT_KEY, JSON.stringify(l));
        else localStorage.removeItem(LAYOUT_KEY);
      } catch {
        /* quota: the in-memory copy serves this session */
      }
      return l;
    })
    .catch(() => cachedLayout());
  return layoutPromise;
}

/** What stands where, from the container projection, once per page load. */
function loadStock(): Promise<Map<string, BinStock>> {
  if (stockPromise) return stockPromise;
  stockPromise = Api.stock()
    .then((rows) => {
      const m = new Map<string, BinStock>();
      for (const r of rows) {
        if (!r.location_code || !(r.state === "received" || r.state === "stored" || r.state === "picked")) continue;
        const e = m.get(r.location_code) ?? { pallets: 0, cartons: 0, loose: 0 };
        if (r.kind === "logistic-unit") e.pallets++;
        else if (r.kind === "loose") e.loose++;
        else e.cartons++;
        m.set(r.location_code, e);
      }
      return m;
    })
    .catch(() => new Map<string, BinStock>());
  return stockPromise;
}

/**
 * `code` is the location code to fly to; `sub` a line under it (the path); `fallback` is what to
 * show when the scene cannot be (no published layout, the code not in it, WebGL missing).
 */
export function GotoScene({ code, sub, fallback }: { code: string; sub?: string | null | undefined; fallback: ReactNode }) {
  const host = useRef<HTMLDivElement | null>(null);
  // null: still deciding; false: fallback; true: scene (the host div renders, then the effect below mounts into it).
  const [show, setShow] = useState<boolean | null>(null);
  // What the scene needs, gathered once the layout describes the code; the mount effect uses it.
  const [prepared, setPrepared] = useState<{ code: string; options: Omit<SceneOptions, "onError">; mod: typeof import("./goto-scene.js") } | null>(null);
  const [mounted, setMounted] = useState<MountedScene | null>(null);
  // Why the scene stood down on this device (shown small under the fallback so the owner can report it).
  const [why, setWhy] = useState<string | null>(null);

  // Gather: layout (cached, then fresh), the module, the stock. Nothing touches the DOM here.
  useEffect(() => {
    let stop = false;
    setShow(null);
    setPrepared(null);
    setWhy(null);
    (async () => {
      // The server's layout first (one request per couple of minutes), the cached copy only when it cannot be reached.
      const layout = (await loadLayout()) ?? cachedLayout();
      if (stop) return;
      if (!layout) { setShow(false); return; }
      const bundle = loadCachedBundle();
      const cfg = bundle?.siteConfig?.layout;
      const binCode = cfg?.binCode ?? "{aisle}-{bay}-{level}-{compartment}";
      const mod = await import("./goto-scene.js");
      if (stop) return;
      const use: LayoutOut | null = mod.findsBin(layout, binCode, code) ? layout : null;
      if (!use) { setShow(false); return; }
      const stock = await loadStock();
      if (stop) return;
      const rackTypes: RackTypeLike[] = (cfg?.rackTypes ?? []).map((x) => ({ id: x.id, holds: x.holds, height: x.height, levelHeights: x.levelHeights ?? null }));
      const builtIn: RackTypeLike[] = [
        { id: "pallet-eur", holds: "pallet", height: cfg?.rackHeight ?? 5000, levelHeights: null },
        { id: "shelf", holds: "carton", height: 2000, levelHeights: null },
      ];
      setPrepared({ code, mod, options: { layout: use, binCode, rackHeight: cfg?.rackHeight ?? 5000, rackTypes: [...rackTypes, ...builtIn.filter((b) => !rackTypes.some((x) => x.id === b.id))], target: code, stock, reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches } });
      setShow(true);
    })();
    return () => { stop = true; };
  }, [code]);

  // Mount: runs after React has committed the host div (a requestAnimationFrame fired before the
  // commit on iOS Safari and found no div — the box stayed dark with nothing in it, 2026-11-02).
  useEffect(() => {
    if (show !== true || !prepared || prepared.code !== code) return;
    const el = host.current;
    if (!el) { setWhy("ingen ruta att rita i"); setShow(false); return; }
    let scene: MountedScene | null = null;
    let gone = false;
    try {
      scene = prepared.mod.mountGoto(el, { ...prepared.options, onError: (m) => { if (!gone) { setWhy(m); setShow(false); } } });
      setMounted(scene);
    } catch (e) {
      // No WebGL (or the context was lost): today's box instead.
      setWhy(e instanceof Error ? `${e.name}: ${e.message}` : String(e));
      setShow(false);
    }
    return () => { gone = true; scene?.dispose(); setMounted(null); };
  }, [show, prepared, code]);

  if (show !== true) return <>{fallback}{why && <p className="hint" style={{ fontSize: 11, opacity: 0.7 }}>3D: {why}</p>}</>;
  return (
    <div className="goto3d" ref={host} role="img" aria-label={code}>
      <div className="code">{code}</div>
      {sub ? <div className="sub">{sub}</div> : null}
      {/* A tap anywhere on the scene replays the flight (owner 2026-11-02: that is enough, no button). */}
    </div>
  );
}
